import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, User, Globe, LogOut, Settings, Award } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';
import { useAuth } from '../context/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { language, toggleLanguage } = useLanguage();
  const { user, logout, isAuthenticated } = useAuth();
  const t = translations[language].nav;

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_innovate-nexus-1/artifacts/hxel7nka_BRAND2020_1.png" 
              alt="ColaboraChile"
              className="h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/programs"
              className={`text-sm font-medium transition-colors hover:text-gray-600 ${
                isActive('/programs') ? 'text-black' : 'text-gray-700'
              }`}
            >
              {t.programs}
            </Link>
            <Link
              to="/pricing"
              className={`text-sm font-medium transition-colors hover:text-gray-600 ${
                isActive('/pricing') ? 'text-black' : 'text-gray-700'
              }`}
            >
              {language === 'es' ? 'Precios' : 'Pricing'}
            </Link>
            <Link
              to="/team"
              data-testid="nav-team-link"
              className={`text-sm font-medium transition-colors hover:text-gray-600 ${
                isActive('/team') ? 'text-black' : 'text-gray-700'
              }`}
            >
              {language === 'es' ? 'Equipo' : 'Team'}
            </Link>
            {/* Separator */}
            <div className="h-6 w-px bg-gray-300" />

            {isAuthenticated ? (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="text-sm font-medium text-gray-700 transition-colors hover:text-gray-600 flex items-center gap-1">
                      <User className="w-5 h-5" />
                      <span className="hidden lg:inline">{user?.name}</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center gap-2 cursor-pointer">
                        <User className="w-4 h-4" />
                        {language === 'es' ? 'Panel' : 'Dashboard'}
                      </Link>
                    </DropdownMenuItem>
                    {user?.role === 'evaluator' && (
                      <DropdownMenuItem asChild>
                        <Link to="/evaluator-dashboard" className="flex items-center gap-2 cursor-pointer">
                          <Award className="w-4 h-4" />
                          {language === 'es' ? 'Evaluar' : 'Evaluate'}
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="flex items-center gap-2 cursor-pointer">
                        <Settings className="w-4 h-4" />
                        {language === 'es' ? 'Mi Perfil' : 'My Profile'}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="text-red-600 cursor-pointer"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      {language === 'es' ? 'Cerrar Sesión' : 'Logout'}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Link
                to="/login"
                data-testid="nav-login-link"
                className="text-sm font-semibold px-4 py-1.5 rounded-full bg-black text-white hover:bg-gray-800 transition-colors"
              >
                {language === 'es' ? 'Iniciar Sesión' : 'Login'}
              </Link>
            )}
            
            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-1 text-sm font-medium text-gray-700 hover:text-primary transition-colors"
            >
              <Globe className="w-4 h-4" />
              <span className="uppercase">{language === 'es' ? 'ES' : 'EN'}</span>
            </button>
            
            {isAuthenticated && user?.role === 'applicant' && (
              <Button
                asChild
                className="bg-secondary text-white hover:bg-secondary/90 transition-colors"
              >
                <Link to="/apply">{t.apply}</Link>
              </Button>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-md text-gray-700 hover:text-black hover:bg-sand-100"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="px-2 pt-2 pb-3 space-y-1">
            <Link
              to="/programs"
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-black hover:bg-sand-100"
              onClick={() => setIsOpen(false)}
            >
              {t.programs}
            </Link>
            <Link
              to="/pricing"
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-black hover:bg-sand-100"
              onClick={() => setIsOpen(false)}
            >
              {language === 'es' ? 'Precios' : 'Pricing'}
            </Link>
            <Link
              to="/team"
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-black hover:bg-sand-100"
              onClick={() => setIsOpen(false)}
            >
              {language === 'es' ? 'Equipo' : 'Team'}
            </Link>
            
            {isAuthenticated && (
              <>
                <Link
                  to="/dashboard"
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-black hover:bg-sand-100"
                  onClick={() => setIsOpen(false)}
                >
                  {t.dashboard}
                </Link>
                
                {user?.role === 'evaluator' && (
                  <Link
                    to="/evaluator-dashboard"
                    className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-black hover:bg-sand-100 flex items-center gap-2"
                    onClick={() => setIsOpen(false)}
                  >
                    <Award className="w-4 h-4" />
                    {language === 'es' ? 'Evaluar' : 'Evaluate'}
                  </Link>
                )}
                
                <Link
                  to="/profile"
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-black hover:bg-sand-100 flex items-center gap-2"
                  onClick={() => setIsOpen(false)}
                >
                  <Settings className="w-4 h-4" />
                  {language === 'es' ? 'Mi Perfil' : 'My Profile'}
                </Link>
              </>
            )}
            
            {/* Mobile Language Switcher */}
            <button
              onClick={() => {
                toggleLanguage();
                setIsOpen(false);
              }}
              className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-black hover:bg-sand-100 flex items-center space-x-2"
            >
              <Globe className="w-5 h-5" />
              <span>{language === 'es' ? 'English' : 'Español'}</span>
            </button>
            
            {isAuthenticated ? (
              <>
                {user?.role === 'applicant' && (
                  <Link
                    to="/apply"
                    className="block px-3 py-2 rounded-md text-base font-medium bg-secondary text-white hover:bg-secondary/90"
                    onClick={() => setIsOpen(false)}
                  >
                    {t.apply}
                  </Link>
                )}
                <button
                  onClick={() => {
                    handleLogout();
                    setIsOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-red-600 hover:bg-red-50 flex items-center gap-2"
                >
                  <LogOut className="w-5 h-5" />
                  {language === 'es' ? 'Cerrar Sesión' : 'Logout'}
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="block px-3 py-2 rounded-full text-center text-base font-semibold bg-black text-white hover:bg-gray-800 mx-2"
                onClick={() => setIsOpen(false)}
              >
                {language === 'es' ? 'Iniciar Sesión' : 'Login'}
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;